// The light map (docs/19 N4–N6): a low-resolution grid (`S` samples per cell) the renderer
// stretches over the board with linear filtering. Two images come out of it each frame:
//   multiply — ambient (with sun/moon shadows baked in) plus every light that reaches the sample;
//              drawn with MULTIPLY over the terrain, so the dark can never go below the floors;
//   glow     — a small additive warm halo close to each flame, capped so nothing washes out.
// Lights are occluded by opaque squares (rays walk the cell grid), so walls throw shadows and a
// torch on a south wall lights the room, not the corridor behind it. Static fields are computed
// once per board; per frame only the flicker factors change.
import type { TileData } from '../../core/sim/types';
import { lightLook } from '../../core/crawl/crawl';
import type { TiledBoard } from '../tiling';
import { frame } from '../tiling/oryx';
import { LIQUID, PROPS } from '../tiling/oryx';
import { liquidFill } from '../tiling/patterns';
import { AMBIENT_FLOOR, LUMA_FLOOR, legible, luma, type DayLight, type RGB } from './daylight';

/** Samples per cell. 4 at 24 px cells = one sample per 6 px, smooth after linear filtering. */
export const SAMPLES = 4;
/** The additive halo never adds more than this per channel. */
export const GLOW_CAP = 0.22;

/** Wall tops take this share of a light: the flame lights the face, not the whole block (bright stone would read as fog). */
const FACE = 0.4;

export type Flicker = 'torch' | 'fire' | 'crystal' | 'steady' | 'sky' | 'lantern' | 'lava';

export interface Light {
  id: string;
  /** Position in cells (fractional; cell centre = x + 0.5). */
  x: number; y: number;
  radius: number;
  colour: RGB;
  intensity: number;
  flicker: Flicker;
  /** Share of the light that also goes into the additive halo (0 = none). */
  glow: number;
}

export interface BoardShape { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> }

const nameSet = (ids: readonly number[]): Set<string> => new Set(ids.map(frame));
const TORCH = nameSet(PROPS.torch);
const FIRE = nameSet([...PROPS.fire, ...PROPS.brazier]);
const CRYSTAL_WARM = nameSet([PROPS.crystals.orange]);
const CRYSTAL_COLD = nameSet(PROPS.crystals.grey);
const LAVA = nameSet(liquidFill(LIQUID.lava!));
const LAVA_COLOUR: RGB = [1.0, 0.5, 0.2];

/** Squares light cannot pass: walls (trees outdoors) and shut doors. */
export const isOpaque = (t: TileData | undefined): boolean => !t || t.terrain === 'Wall' || (t.terrain === 'Door' && !t.doorOpen);

const TORCH_COLOUR: RGB = [1.0, 0.62, 0.30];
const FIRE_COLOUR: RGB = [1.0, 0.52, 0.22];
const LANTERN_COLOUR: RGB = [1.0, 0.86, 0.62];

/**
 * Every light a board gives off: torches and fires the tiler placed, glowing crystals, lava (docs/28:
 * every other lava square, in a checkerboard, so a lake glows evenly without a light per square), burning
 * hazards, objectives (a faint gold so they read in the dark) and — indoors — daylight through
 * the exits, coloured by the hour.
 */
export function findLights(board: BoardShape, tiled: TiledBoard | undefined, day: DayLight): Light[] {
  const out: Light[] = [];
  const { w, h, tiles } = board;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const t = tiles[y * w + x];
      const cell = tiled?.cells[y * w + x];
      const frames = cell ? [...cell.props, ...cell.overlay] : [];
      for (const f of frames) {
        if (TORCH.has(f.frame)) {
          // mounted on a wall run: the flame hangs just off the face that opens onto the room
          // (south first — the face the camera sees); a torch on a buried wall gives no light
          const side = !isOpaque(tiles[(y + 1) * w + x]) && y + 1 < h ? 1.05 : y > 0 && !isOpaque(tiles[(y - 1) * w + x]) ? -0.05 : undefined;
          if (side !== undefined) out.push({ id: `torch:${x},${y}`, x: x + 0.5, y: y + side, radius: 4.6, colour: TORCH_COLOUR, intensity: 0.62 * day.torch, flicker: 'torch', glow: 0.35 });
        } else if (FIRE.has(f.frame)) {
          out.push({ id: `fire:${x},${y}`, x: x + 0.5, y: y + 0.6, radius: 4, colour: FIRE_COLOUR, intensity: 0.7 * day.torch, flicker: 'fire', glow: 0.4 });
        } else if (CRYSTAL_WARM.has(f.frame)) {
          out.push({ id: `crystal:${x},${y}`, x: x + 0.5, y: y + 0.5, radius: 2.2, colour: [1.0, 0.55, 0.25], intensity: 0.45, flicker: 'crystal', glow: 0.35 });
        } else if (CRYSTAL_COLD.has(f.frame)) {
          out.push({ id: `crystal:${x},${y}`, x: x + 0.5, y: y + 0.5, radius: 2, colour: [0.6, 0.8, 1.0], intensity: 0.35, flicker: 'crystal', glow: 0.3 });
        }
      }
      if (cell && (x + y) % 2 === 0 && cell.ground.some((f) => LAVA.has(f.frame))) {
        out.push({ id: `lava:${x},${y}`, x: x + 0.5, y: y + 0.5, radius: 2.6, colour: LAVA_COLOUR, intensity: 0.4, flicker: 'lava', glow: 0.25 });
      }
      if (!t) continue;
      if (t.hazard === 'Fire' || t.hazard === 'Burning') {
        out.push({ id: `hazard:${x},${y}`, x: x + 0.5, y: y + 0.5, radius: 3, colour: FIRE_COLOUR, intensity: 0.55 * day.torch, flicker: 'fire', glow: 0.35 });
      }
      if (t.objective) {
        out.push({ id: `objective:${x},${y}`, x: x + 0.5, y: y + 0.5, radius: 1.8, colour: [1.0, 0.86, 0.5], intensity: 0.4, flicker: 'crystal', glow: 0.15 });
      }
      if (t.exit && day.setting === 'indoor') {
        out.push({ id: `sky:${x},${y}`, x: x + 0.5, y: y + 0.5, radius: 3.6, colour: day.sky, intensity: 0.75 * day.skyStrength, flicker: 'sky', glow: 0.12 });
      }
    }
  }
  return out;
}

/**
 * docs/60 DC7: a crawl's own lights (sconces, braziers, candles, glowing moss, a shrine's glow, ember
 * cracks). Most hang on a wall square: like a tiler torch, the light sits just off the face that opens
 * onto the room (south first, then north, east, west), so it lights the chamber and not the rock.
 */
export function crawlLights(board: BoardShape, lights: ReadonlyArray<{ tile: { x: number; y: number }; radius: number; look: number }>, day: DayLight): Light[] {
  const { w, h, tiles } = board;
  const open = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && !isOpaque(tiles[y * w + x]) && tiles[y * w + x]?.terrain !== 'Void';
  const out: Light[] = [];
  for (const l of lights) {
    const { x, y } = l.tile;
    const def = lightLook(l.look);
    let lx = x + 0.5, ly = y + 0.5;
    if (!open(x, y)) {
      if (open(x, y + 1)) ly = y + 1.05;
      else if (open(x, y - 1)) ly = y - 0.05;
      else if (open(x + 1, y)) lx = x + 1.05;
      else if (open(x - 1, y)) lx = x - 0.05;
      else continue;   // buried in the rock: gives no light
    }
    // fire is brighter in the dark (as torches are); moss and shrines glow the same at any hour
    const k = def.flicker === 'torch' || def.flicker === 'fire' ? Math.max(0.8, day.torch) : 1;
    out.push({ id: `crawl:${x},${y}`, x: lx, y: ly, radius: Math.max(2, l.radius + 1.6), colour: def.colour, intensity: def.intensity * k, flicker: def.flicker, glow: def.glow });
  }
  return out;
}

/** A hero's lantern: warm, short, only when the room or the night is dark. */
export function lantern(id: string, x: number, y: number, day: DayLight): Light | undefined {
  if (day.lantern <= 0.02) return undefined;
  return { id: `lantern:${id}`, x, y, radius: 3.5, colour: LANTERN_COLOUR, intensity: 0.5 * day.lantern, flicker: 'lantern', glow: 0 };
}

/** Flicker factor of a light at time `t` seconds (deterministic per light id; 1 when still). */
export function flicker(l: Light, t: number, still: boolean): number {
  if (still) return 1;
  let ph = 0;
  for (let i = 0; i < l.id.length; i++) ph = (ph * 31 + l.id.charCodeAt(i)) % 6283;
  ph /= 1000;
  switch (l.flicker) {
    case 'torch': return 1 + 0.07 * Math.sin(t * 7.3 + ph) + 0.05 * Math.sin(t * 13.1 + 2 * ph) + 0.04 * Math.sin(t * 23.7 + 3 * ph);
    case 'fire': return 1 + 0.1 * Math.sin(t * 9.1 + ph) + 0.07 * Math.sin(t * 17.3 + 2 * ph) + 0.05 * Math.sin(t * 29.9 + ph);
    case 'crystal': return 1 + 0.12 * Math.sin(t * 1.3 + ph);
    case 'lava': return 1 + 0.08 * Math.sin(t * 1.1 + ph) + 0.05 * Math.sin(t * 2.7 + 2 * ph);
    case 'sky': return 1 + 0.03 * Math.sin(t * 0.4 + ph);
    case 'lantern': return 1 + 0.03 * Math.sin(t * 5.1 + ph);
    default: return 1;
  }
}

/** A part of the map, in samples (end exclusive). */
export interface MapRegion { i0: number; j0: number; i1: number; j1: number }

/** One light's reach: the samples it touches and their weights (falloff × occlusion). */
interface Field { idx: Uint32Array; wt: Float32Array }

export class LightMap {
  readonly gw: number; readonly gh: number;
  /** Ambient × shadow, floors applied (RGB per sample). */
  readonly base: Float32Array;
  /** Outdoor shadow amount per sample (0..1), for tests and the shadow overlay. */
  readonly shade: Float32Array;
  readonly multiply: Uint8ClampedArray<ArrayBuffer>;
  readonly glow: Uint8ClampedArray<ArrayBuffer>;
  private readonly opaque: Uint8Array;
  /** Per cell: how much light a square takes — 1 open, FACE for a wall with an open side, 0 for buried rock. */
  private readonly take: Float32Array;
  private fields = new Map<string, Field>();
  private acc: Float32Array;
  private gacc: Float32Array;

  /**
   * `shafts`: sun shafts through a canopy — soft bands along the sun's direction, drawn into the
   * halo only where the sun reaches (so the trees' shadows cut them) and clipped to the board.
   */
  constructor(readonly board: BoardShape, readonly day: DayLight, readonly opts: { shafts?: boolean } = {}, readonly S = SAMPLES) {
    const { w, h, tiles } = board;
    this.gw = w * S; this.gh = h * S;
    const n = this.gw * this.gh;
    this.opaque = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) this.opaque[i] = isOpaque(tiles[i]) ? 1 : 0;
    this.take = new Float32Array(w * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (!this.opaque[i]) { this.take[i] = 1; continue; }
      const openSide = [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => { const nx = x + dx!, ny = y + dy!; return nx >= 0 && ny >= 0 && nx < w && ny < h && !this.opaque[ny * w + nx]; });
      this.take[i] = openSide ? FACE : 0;
    }
    this.base = new Float32Array(n * 3);
    this.shade = new Float32Array(n);
    this.acc = new Float32Array(n * 3);
    this.gacc = new Float32Array(n * 3);
    this.multiply = new Uint8ClampedArray(n * 4);
    this.glow = new Uint8ClampedArray(n * 4);
    this.bakeBase();
  }

  private solid(cx: number, cy: number): boolean {
    if (cx < 0 || cy < 0 || cx >= this.board.w || cy >= this.board.h) return this.day.setting === 'indoor';
    return this.opaque[cy * this.board.w + cx] === 1;
  }

  /** Ambient, darkened where the sun (or moon) is blocked by trees and walls — the long dawn shadows. */
  private bakeBase(): void {
    const { gw, gh, S, day } = this;
    const sun = day.sun;
    const floor = legible([AMBIENT_FLOOR, AMBIENT_FLOOR, AMBIENT_FLOOR]);
    for (let j = 0; j < gh; j++) {
      for (let i = 0; i < gw; i++) {
        const k = j * gw + i;
        let shade = 0;
        if (sun && sun.strength > 0) {
          const px = (i + 0.5) / S, py = (j + 0.5) / S;
          const own = { x: Math.floor(px), y: Math.floor(py) };
          // march toward the light; the first occluder within its shadow length shades this sample
          const reach = sun.length * 1.1, step = 1 / S;
          for (let d = step; d <= reach; d += step) {
            const cx = Math.floor(px - sun.dx * d), cy = Math.floor(py - sun.dy * d);
            if (cx === own.x && cy === own.y) continue;
            if (cx < 0 || cy < 0 || cx >= this.board.w || cy >= this.board.h) break;
            if (this.opaque[cy * this.board.w + cx]) { shade = sun.strength * (1 - 0.45 * (d / reach)); break; }
          }
        }
        this.shade[k] = shade;
        let c: RGB = [day.ambient[0] * (1 - shade), day.ambient[1] * (1 - shade), day.ambient[2] * (1 - shade)];
        if (shade > 0) {
          // shadows deepen but never past the legibility floor; they keep a cool tint
          c = [c[0] * 0.97, c[1] * 0.99, Math.min(1, c[2] * 1.06)];
          if (c[0] < floor[0] || c[1] < floor[1] || c[2] < floor[2] || luma(c) < LUMA_FLOOR) c = legible(c);
        }
        this.base[k * 3] = c[0]; this.base[k * 3 + 1] = c[1]; this.base[k * 3 + 2] = c[2];
      }
    }
  }

  /** Is the straight line from the light to (px, py) clear? Walls the ray *ends* in are lit (their faces). */
  private clear(l: Light, px: number, py: number): boolean {
    const tx = Math.floor(px), ty = Math.floor(py);
    const dx = px - l.x, dy = py - l.y;
    const steps = Math.ceil(Math.hypot(dx, dy) * this.S * 1.5);
    for (let s = 1; s < steps; s++) {
      const cx = Math.floor(l.x + (dx * s) / steps), cy = Math.floor(l.y + (dy * s) / steps);
      if (cx === tx && cy === ty) return true;
      if (this.solid(cx, cy)) return false;
    }
    return true;
  }

  private field(l: Light): Field {
    const key = `${l.id}@${Math.round(l.x * this.S)},${Math.round(l.y * this.S)},${l.radius}`;
    const hit = this.fields.get(key);
    if (hit) return hit;
    const { gw, gh, S } = this;
    const idx: number[] = [], wt: number[] = [];
    const i0 = Math.max(0, Math.floor((l.x - l.radius) * S)), i1 = Math.min(gw - 1, Math.ceil((l.x + l.radius) * S));
    const j0 = Math.max(0, Math.floor((l.y - l.radius) * S)), j1 = Math.min(gh - 1, Math.ceil((l.y + l.radius) * S));
    const r2 = l.radius * l.radius;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const px = (i + 0.5) / S, py = (j + 0.5) / S;
        const d2 = (px - l.x) ** 2 + (py - l.y) ** 2;
        if (d2 >= r2) continue;
        const take = this.take[Math.floor(py) * this.board.w + Math.floor(px)]!;
        if (take === 0 || !this.clear(l, px, py)) continue;
        const f = 1 - d2 / r2;
        idx.push(j * gw + i); wt.push(f * f * take);
      }
    }
    const out = { idx: Uint32Array.from(idx), wt: Float32Array.from(wt) };
    // moving lanterns make a new key every few frames; keep the cache bounded
    if (this.fields.size > 256) this.fields.clear();
    this.fields.set(key, out);
    return out;
  }

  /**
   * Compose both images for time `t` (seconds). `strength` 0..1 scales the whole effect towards
   * flat white (the "soft" setting); `still` freezes flicker (reduce motion). `region` (samples,
   * end exclusive) composes only that part — what the camera shows — and leaves the rest as the
   * last compose left it: every sample is a sum over the lights at that sample alone, so the part
   * composed is exactly what a whole compose would make of it.
   */
  compose(lights: readonly Light[], t: number, opts: { strength?: number; still?: boolean } = {}, region?: MapRegion): void {
    const strength = opts.strength ?? 1, still = !!opts.still;
    const gw = this.gw;
    const r = region ? { i0: Math.max(0, region.i0), j0: Math.max(0, region.j0), i1: Math.min(gw, region.i1), j1: Math.min(this.gh, region.j1) } : { i0: 0, j0: 0, i1: gw, j1: this.gh };
    if (r.i1 <= r.i0 || r.j1 <= r.j0) return;
    const acc = this.acc, gacc = this.gacc;
    for (let j = r.j0; j < r.j1; j++) {
      const a = (j * gw + r.i0) * 3, b = (j * gw + r.i1) * 3;
      acc.set(this.base.subarray(a, b), a);
      gacc.fill(0, a, b);
    }
    const whole = r.i0 === 0 && r.j0 === 0 && r.i1 === gw && r.j1 === this.gh;
    for (const l of lights) {
      // a light whose reach misses the region adds nothing to it (and needs no field worked out)
      if (!whole && ((l.x + l.radius) * this.S < r.i0 || (l.x - l.radius) * this.S >= r.i1 || (l.y + l.radius) * this.S < r.j0 || (l.y - l.radius) * this.S >= r.j1)) continue;
      const k = l.intensity * flicker(l, t, still);
      const g = k * l.glow;
      const f = this.field(l);
      const [cr, cg, cb] = l.colour;
      for (let q = 0; q < f.idx.length; q++) {
        const p = f.idx[q]!;
        if (!whole) { const j = (p / gw) | 0, i = p - j * gw; if (i < r.i0 || i >= r.i1 || j < r.j0 || j >= r.j1) continue; }
        const s = p * 3, wv = f.wt[q]!;
        acc[s] = acc[s]! + cr * wv * k; acc[s + 1] = acc[s + 1]! + cg * wv * k; acc[s + 2] = acc[s + 2]! + cb * wv * k;
        if (g > 0) { const w2 = wv * wv * g; gacc[s] = gacc[s]! + cr * w2; gacc[s + 1] = gacc[s + 1]! + cg * w2; gacc[s + 2] = gacc[s + 2]! + cb * w2; }
      }
    }
    const sun = this.day.sun;
    if (this.opts.shafts && sun && !sun.moon) {
      // bands run along the shadow direction; across them, a few soft stripes that breathe slowly
      const nx = -sun.dy, ny = sun.dx, sky = this.day.sky, k = 0.16 * this.day.skyStrength;
      for (let j = r.j0; j < r.j1; j++) for (let i = r.i0; i < r.i1; i++) {
        const p = j * gw + i;
        const lit = 1 - this.shade[p]! / Math.max(0.01, sun.strength);
        if (lit <= 0) continue;
        const v = ((i + 0.5) / this.S) * nx + ((j + 0.5) / this.S) * ny;
        const band = Math.floor(v / 4.5);
        const phase = (v / 4.5 - band) * Math.PI;
        const on = (band * 7919) % 3 !== 0 ? Math.sin(phase) ** 6 : 0;   // two bands in three
        if (on <= 0.01) continue;
        const breathe = still ? 1 : 0.65 + 0.35 * Math.sin(t * 0.35 + band * 1.7);
        const a = k * on * breathe * lit;
        gacc[p * 3] = gacc[p * 3]! + sky[0] * a; gacc[p * 3 + 1] = gacc[p * 3 + 1]! + sky[1] * a; gacc[p * 3 + 2] = gacc[p * 3 + 2]! + sky[2] * a * 0.8;
      }
    }
    const m = this.multiply, gl = this.glow;
    for (let j = r.j0; j < r.j1; j++) {
      for (let p = j * gw + r.i0, end = j * gw + r.i1; p < end; p++) {
        const s = p * 3, o = p * 4;
        for (let c = 0; c < 3; c++) {
          const v = Math.min(1, acc[s + c]!);
          m[o + c] = (1 - strength * (1 - v)) * 255;
          gl[o + c] = Math.min(GLOW_CAP, gacc[s + c]!) * strength * 255;
        }
        m[o + 3] = 255; gl[o + 3] = 255;
      }
    }
  }

  /** The light level at a board point (cells), from the last compose: for tinting units. */
  sample(x: number, y: number): RGB {
    const i = Math.max(0, Math.min(this.gw - 1, Math.floor(x * this.S))), j = Math.max(0, Math.min(this.gh - 1, Math.floor(y * this.S)));
    const o = (j * this.gw + i) * 4;
    return [this.multiply[o]! / 255, this.multiply[o + 1]! / 255, this.multiply[o + 2]! / 255];
  }

  /** The darkest the multiply image gets (luminance), from the last compose. */
  minLuma(): number {
    let min = 1;
    for (let p = 0; p < this.gw * this.gh; p++) {
      const o = p * 4;
      min = Math.min(min, luma([this.multiply[o]! / 255, this.multiply[o + 1]! / 255, this.multiply[o + 2]! / 255]));
    }
    return min;
  }
}
